import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  alpha,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Percent,
  Groups,
  AccountBalanceWallet,
  Check,
  Close,
  ContentCopy,
  EmojiEvents,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const emptyForm = { name: "", email: "", password: "", phone: "", document: "", pixKey: "", status: "active" };

const Affiliates = () => {
  const { user } = useContext(AuthContext);

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog afiliado
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Dialog comissões
  const [commAffiliate, setCommAffiliate] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Dialog financeiro
  const [financeOpen, setFinanceOpen] = useState(false);
  const [summary, setSummary] = useState([]);
  const [payoutReqs, setPayoutReqs] = useState([]);
  const [loadingFinance, setLoadingFinance] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliates");
      setAffiliates(data.affiliates || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user.super) load();
    // eslint-disable-next-line
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (aff) => {
    setEditing(aff);
    setForm({ ...emptyForm, ...aff, password: "" });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email) return toast.warn("Nome e email são obrigatórios.");
    if (!editing && !form.password) return toast.warn("Defina uma senha para o afiliado.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/affiliates/${editing.id}`, form);
        toast.success("Afiliado atualizado!");
      } else {
        await api.post("/affiliates", form);
        toast.success("Afiliado criado!");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este afiliado?")) return;
    try {
      await api.delete(`/affiliates/${id}`);
      toast.success("Afiliado removido.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const openCommissions = async (aff) => {
    setCommAffiliate(aff);
    setLoadingPlans(true);
    try {
      const { data } = await api.get(`/affiliates/${aff.id}/plans`);
      setPlans(data);
    } catch (err) {
      toastError(err);
    }
    setLoadingPlans(false);
  };

  const updatePlanField = (planId, field, value) => {
    setPlans((prev) => prev.map((p) => (p.planId === planId ? { ...p, [field]: value } : p)));
  };

  const savePlanCommission = async (plan) => {
    try {
      await api.post(`/affiliates/${commAffiliate.id}/plans`, {
        planId: plan.planId,
        commissionType: plan.commissionType,
        commissionValue: Number(plan.commissionValue) || 0,
        recurrence: plan.recurrence,
        implementationCost:
          plan.implementationCost === "" || plan.implementationCost === null
            ? null
            : Number(plan.implementationCost),
      });
      toast.success(`Comissão do plano ${plan.planName} salva!`);
      updatePlanField(plan.planId, "configured", true);
    } catch (err) {
      toastError(err);
    }
  };

  // ── Financeiro ────────────────────────────────────────────────
  const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const brl3 = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  const loadFinance = async () => {
    setLoadingFinance(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/affiliates/financial-summary"),
        api.get("/affiliates/payout-requests"),
      ]);
      setSummary(s.data || []);
      setPayoutReqs(p.data || []);
    } catch (err) {
      toastError(err);
    }
    setLoadingFinance(false);
  };

  const openFinance = () => { setFinanceOpen(true); loadFinance(); };

  const processPayout = async (id, action) => {
    const label = action === "confirm" ? "confirmar o pagamento" : "rejeitar";
    if (!window.confirm(`Deseja ${label} desta solicitação?`)) return;
    try {
      await api.post(`/affiliates/payout-requests/${id}/process`, { action });
      toast.success(action === "confirm" ? "Pagamento confirmado!" : "Solicitação rejeitada.");
      loadFinance();
    } catch (err) {
      toastError(err);
    }
  };

  const copyPix = (pix) => { navigator.clipboard.writeText(pix || ""); toast.success("Chave PIX copiada!"); };

  if (!user.super) return <ForbiddenPage />;

  return (
    <MainContainer>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Cabeçalho */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: alpha("#5852ab", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Groups sx={{ color: "#5852ab" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Afiliados</Typography>
              <Typography variant="body2" color="text.secondary">Cadastre afiliados e configure a comissão de cada um por plano</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" startIcon={<AccountBalanceWallet />} onClick={openFinance} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
              Financeiro
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
              Novo Afiliado
            </Button>
          </Stack>
        </Stack>

        {/* Lista */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Telefone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiliates.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.email}</TableCell>
                    <TableCell>{a.phone || "-"}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={a.status === "active" ? "Ativo" : "Inativo"}
                        sx={{ background: a.status === "active" ? alpha("#2e7d32", 0.15) : alpha("#757575", 0.15), color: a.status === "active" ? "#2e7d32" : "#757575", fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Comissões por plano">
                        <IconButton size="small" onClick={() => openCommissions(a)}><Percent fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => handleDelete(a.id)}><Delete fontSize="small" color="error" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && affiliates.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>Nenhum afiliado cadastrado.</TableCell></TableRow>
                )}
                {loading && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </Card>
      </Box>

      {/* Dialog criar/editar afiliado */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? "Editar Afiliado" : "Novo Afiliado"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><TextField label="Nome" size="small" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email" size="small" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={editing ? "Nova senha (deixe vazio p/ manter)" : "Senha"} type="password" size="small" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Telefone" size="small" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Documento (CPF/CNPJ)" size="small" fullWidth value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Chave PIX (p/ receber)" size="small" fullWidth value={form.pixKey} onChange={(e) => setForm({ ...form, pixKey: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" size="small" fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog comissões por plano */}
      <Dialog open={!!commAffiliate} onClose={() => setCommAffiliate(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Comissões — {commAffiliate?.name}</DialogTitle>
        <DialogContent>
          {loadingPlans ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Plano</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Recorrência</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Custo Implant.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">—</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.planId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.planName}</Typography>
                        <Typography variant="caption" color="text.secondary">R$ {p.planAmount}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={p.commissionType} onChange={(e) => updatePlanField(p.planId, "commissionType", e.target.value)} sx={{ minWidth: 90 }}>
                          <MenuItem value="percent">%</MenuItem>
                          <MenuItem value="fixed">R$ fixo</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={p.commissionValue} onChange={(e) => updatePlanField(p.planId, "commissionValue", e.target.value)} sx={{ width: 100 }}
                          inputProps={{ step: 0.001 }}
                          InputProps={p.commissionType === "percent" ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : { startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={p.recurrence} onChange={(e) => updatePlanField(p.planId, "recurrence", e.target.value)} sx={{ minWidth: 120 }}>
                          <MenuItem value="recurring">Recorrente</MenuItem>
                          <MenuItem value="once">Única</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" placeholder={`padrão: ${p.planImplementationCost}`}
                          value={p.implementationCost === null ? "" : p.implementationCost}
                          onChange={(e) => updatePlanField(p.planId, "implementationCost", e.target.value)} sx={{ width: 110 }}
                          inputProps={{ step: 0.001 }}
                          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant={p.configured ? "outlined" : "contained"} onClick={() => savePlanCommission(p)} sx={{ textTransform: "none", borderRadius: 2 }}>
                          Salvar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommAffiliate(null)} sx={{ textTransform: "none" }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Financeiro */}
      <Dialog open={financeOpen} onClose={() => setFinanceOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 700 }}>Financeiro dos Afiliados</DialogTitle>
        <DialogContent>
          {loadingFinance ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Solicitações de saque pendentes */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Solicitações de pagamento</Typography>
              <Box sx={{ overflowX: "auto", mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Afiliado</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Chave PIX</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Data</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payoutReqs.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.affiliateName}</TableCell>
                        <TableCell>
                          {r.pixKey ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{r.pixKey}</Typography>
                              <IconButton size="small" onClick={() => copyPix(r.pixKey)}><ContentCopy sx={{ fontSize: 15 }} /></IconButton>
                            </Stack>
                          ) : <Typography variant="caption" color="text.secondary">sem chave</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{brl3(r.amount)}</TableCell>
                        <TableCell align="center">{moment(r.createdAt).format("DD/MM/YYYY")}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={r.status === "paid" ? "Pago" : r.status === "rejected" ? "Rejeitado" : "Aguardando"}
                            sx={{ background: alpha(r.status === "paid" ? "#2e7d32" : r.status === "rejected" ? "#c62828" : "#f9a825", 0.15), color: r.status === "paid" ? "#2e7d32" : r.status === "rejected" ? "#c62828" : "#854f0b", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="center">
                          {r.status === "pending" && (
                            <>
                              <Tooltip title="Confirmar pagamento"><IconButton size="small" onClick={() => processPayout(r.id, "confirm")}><Check fontSize="small" sx={{ color: "#2e7d32" }} /></IconButton></Tooltip>
                              <Tooltip title="Rejeitar"><IconButton size="small" onClick={() => processPayout(r.id, "reject")}><Close fontSize="small" color="error" /></IconButton></Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {payoutReqs.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhuma solicitação.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Box>

              {/* Ranking / resumo por afiliado */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Resumo por afiliado (ranking por renda)</Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Afiliado</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Chave PIX</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Empresas</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Renda mensal</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">A pagar</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Já pago</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.map((a, i) => (
                      <TableRow key={a.affiliateId} hover>
                        <TableCell>{i === 0 ? <EmojiEvents sx={{ color: "#f9a825", fontSize: 20 }} /> : i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: i === 0 ? 700 : 400 }}>{a.name}</TableCell>
                        <TableCell>
                          {a.pixKey ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{a.pixKey}</Typography>
                              <IconButton size="small" onClick={() => copyPix(a.pixKey)}><ContentCopy sx={{ fontSize: 15 }} /></IconButton>
                            </Stack>
                          ) : <Typography variant="caption" color="text.secondary">-</Typography>}
                        </TableCell>
                        <TableCell align="center">{a.numEmpresas}</TableCell>
                        <TableCell align="right">{brl(a.mrr)}</TableCell>
                        <TableCell align="right" sx={{ color: "#854f0b", fontWeight: 700 }}>{brl3(a.aPagar)}</TableCell>
                        <TableCell align="right" sx={{ color: "#2e7d32" }}>{brl3(a.jaPago)}</TableCell>
                      </TableRow>
                    ))}
                    {summary.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhum afiliado.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinanceOpen(false)} sx={{ textTransform: "none" }}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Affiliates;
